import AppCommandBar from 'app/AppCommandBar'
import { AutoStack } from 'components'
import { RichTextViewer } from 'components/RichTextViewer/RichTextViewer'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  AmountFormSchema,
  OrderIdDistributionFormType,
  ProvingDetailsAmountForm,
  ProvingDetailsOrderIdDistributionForm,
  ProvingDetailsOrderIdDistributionFormSchema,
  ProvingDetailsPlausabilityCheckForm,
  ProvingDetailsResourceRequirementForm,
  ProvingDetailsResourceRequirementFormSchema,
  ProvingDetailsResourceRequirementFormValues,
  ProvingDetailsResourceRequirementStatusForm,
  ProvingDetailsTimespanForm,
  ProvingDetailsTimespanFormSchema,
  ResourceRequirementDetailsForm,
  ResourceRequirementDetailsFormSchema,
} from 'forms/ProvingDetailsForm'
import { ProvingDetailsResourceBookingFormValues } from 'forms/ProvingDetailsForm/resourceBookings/ProvingDetailsResourceBookingForm'
import { ProvingDetailsNmfForm } from 'forms/ProvingDetailsForm/resourceRequirements/ProvingDetailsNmfForm'
import { ProvingDetailsSpecialUsageForm } from 'forms/ProvingDetailsForm/resourceRequirements/ProvingDetailsSpecialUsageForm'
import { ResourceSearchForm } from 'forms/ProvingDetailsForm/resourceRequirements/ResourceSearchForm'
import { ProvingDetailsAnnotationForm } from 'forms/ProvingDetailsForm/shared/ProvingDetailsAnnotationForm'
import {
  canUpdateProving,
  clearObjectIdsByPath,
  isInProvingRole,
  omitDeep,
  removeNullProperties,
} from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCommand, useDeselectCommand } from 'hooks/useCommand'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import useSelection from 'hooks/useSelection'
import {
  ProvingUpdateType,
  ProvingUserRole,
  Resource,
  ResourceBookingAddInput,
  ResourceRequirement,
  ResourceRequirementBatchInput,
  ResourceRequirementStatus,
  ResourceRequirementUpdateInput,
  ResourceSelectionType,
  UserRole,
} from 'model'
import {
  Icon,
  IconButton,
  Label,
  mergeStyles,
  Panel,
  PanelType,
  Stack,
} from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import { NormalPanel } from 'panels/NormalPanel'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'theme'
import {
  useAddResourceBookingMutation,
  useAddResourceRequirementMutation,
  useChargingInfrastructuresQuery,
  useDeleteResourceRequirementsMutation,
  useFuelsQuery,
  useTestTracksQuery,
  useTestTrackUsageQuery,
  useUpdateProvingMutation,
  useUpdateResourceRequirementMutation,
} from './hooks/index'
import { Page, Section } from './Layout'
import { ParticipantList, ResourceRequirementList } from './lists/index'

enum FormType {
  addResourceRequirement,
  amount,
  timespan,
  orderId,
  status,
  nmf,
  specialUsage,
  check,
  undefined,
  annotation,
  resourceSearch,
}

export const PageResourceRequirements: React.FC = () => {
  const { t } = useTranslation()
  const [proving, refetch] = useCurrentProving()
  const resourceRequirements = proving.resourceRequirements || []
  const [selection, selectedItems] = useSelection<ResourceRequirement>({
    getKey: (r) => r._id,
  })
  const theme = useTheme()
  const [formType, setFormType] = React.useState<FormType>()
  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [dialogState, showDialog, hideDialog] = useDialogState()
  const [timespanPanelState, showTimespanPanel, hideTimespanPanel] =
    useFormikPanelState()
  const [annotationPanelState, showAnnotationPanel, hideAnnotationPanel] =
    useFormikPanelState()
  const [multiEditPanelState, showMultiEditPanel, hideMultiEditPanel] =
    useFormikPanelState()
  const [resourceSearchPanelState, showResourceSearchPanel] =
    useFormikPanelState()
  const { data } = useTestTracksQuery({
    variables: { provingGroundId: proving!.provingGround!._id },
  })
  const testTrackUsage = useTestTrackUsageQuery({
    variables: { provingGroundId: proving!.provingGround!._id },
  })
  const fuelData = useFuelsQuery({
    variables: { provingGroundId: proving!.provingGround!._id },
  })
  const fuels =
    fuelData.data && fuelData.data.resources ? fuelData.data.resources : []
  const chargingInfrastructureData = useChargingInfrastructuresQuery({
    variables: { provingGroundId: proving!.provingGround!._id },
  })
  const chargingInfrastructures =
    chargingInfrastructureData.data && chargingInfrastructureData.data.resources
      ? chargingInfrastructureData.data.resources
      : []
  const testTrackUsages =
    testTrackUsage.data && testTrackUsage.data.resources
      ? testTrackUsage.data.resources
      : []

  const testTracks = data && data.resources ? data.resources : []
  const participants = proving.participants || []
  const [updateProving] = useUpdateProvingMutation()
  const [addResourceRequirement] = useAddResourceRequirementMutation()
  const [updateResourceRequirement] = useUpdateResourceRequirementMutation()
  const [deleteResourceRequirements] = useDeleteResourceRequirementsMutation()
  let canUpdateCurrentProving = canUpdateProving(proving)
  const isProvingAdmin = isInProvingRole(proving, ProvingUserRole.Administrator)
  const isResourceManager = isInProvingRole(
    proving,
    ProvingUserRole.ResourceManager
  )
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )

  const canUseCommand = canUpdateCurrentProving && !isMaintenanceHead

  const [editPanelState, showEditPanel, hideEditPanel] = useFormikPanelState()
  const [selectedItemId, setSelectedItemId] = React.useState()
  let orderIdInitialValues = {}
  React.useEffect(() => {
    if (formType === FormType.orderId && selectedItemId !== undefined) {
      orderIdInitialValues = {
        initialValues: {
          orderIdDistribution: omitDeep(
            proving.orderIdDistribution,
            '__typename'
          ).filter((orderIdProving) => {
            const selectedResReq = resourceRequirements.find(
              (resReq) => resReq._id === selectedItemId
            )
            if (selectedResReq && selectedResReq.orderIdDistribution) {
              if (
                selectedResReq.orderIdDistribution.some(
                  (e) => e._id === orderIdProving._id
                )
              ) {
                return true
              } else {
                return false
              }
            } else {
              return false
            }
          }),
        },
      }
    }
  }, [formType, selectedItemId])

  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  if (isServiceTeam === true) {
    canUpdateCurrentProving = isServiceTeam
  }
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isProvingResourceManager = isInProvingRole(
    proving,
    ProvingUserRole.ResourceManager
  )
  const isProvingManager = isInProvingRole(
    proving,
    ProvingUserRole.ProvingManager
  )

  const isProvingEditor = isInProvingRole(proving, ProvingUserRole.Editor)
  const canUpdateStatus = () => {
    if (isAdmin || isProvingResourceManager || isProvingEditor) {
      return true
    } else {
      return false
    }
  }
  const userCanUpdate = () => {
    if (isAdmin || isProvingResourceManager || isProvingEditor) {
      return true
    } else if (
      isProvingManager &&
      (proving.status === 1 ||
        proving.status === 4 ||
        proving.status === 5 ||
        proving.status === 10)
    ) {
      return true
    } else {
      return false
    }
  }
  const handleSubmitNmf = React.useCallback(
    async (
      values: ProvingUpdateType,
      actions: FormikActions<ProvingUpdateType>
    ) => {
      actions.setSubmitting(true)

      const { errors } = await updateProving({
        variables: { id: proving.id, update: values },
      })
      if (errors) {
        throw errors
      }

      actions.setSubmitting(false)
      hidePanel()
      refetch()
    },
    [proving, updateProving, hidePanel, refetch]
  )

  const handleSubmitSpecialUsage = React.useCallback(
    async (
      values: ProvingUpdateType,
      actions: FormikActions<ProvingUpdateType>
    ) => {
      actions.setSubmitting(true)

      const { errors } = await updateProving({
        variables: { id: proving.id, update: values },
      })

      if (errors) {
        throw errors
      }

      actions.setSubmitting(false)
      hidePanel()
      refetch()
    },
    [proving, updateProving, hidePanel, refetch]
  )

  const handleSubmit = React.useCallback(
    async (
      values: ProvingDetailsResourceRequirementFormValues,
      actions: FormikActions<ProvingDetailsResourceRequirementFormValues>
    ) => {
      actions.setSubmitting(true)
      const { errors } = await addResourceRequirement({
        variables: {
          provingId: proving._id,
          input: omitDeep(values, ['notification']),
        },
      })
      if (errors) {
        throw errors
      }

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hidePanel()
    },
    [proving, addResourceRequirement, hidePanel, refetch, selection]
  )

  const handleSubmitMulti = React.useCallback(
    async (
      values: ResourceRequirementUpdateInput,
      actions: FormikActions<ResourceRequirementUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const updatedValues = removeNullProperties({
        orderIdDistribution: values.orderIdDistribution,
        ...values,
      })
      const batches: ResourceRequirementBatchInput[] = []
      selectedItems.forEach(async (rr) => {
        batches.push({ _id: rr._id, update: updatedValues })
      })
      const { errors } = await updateResourceRequirement({
        variables: { provingId: proving._id, batch: batches },
      })
      if (errors) {
        throw errors
      }
      selection.setAllSelected(false)
      refetch()
      actions.setSubmitting(false)
      hideMultiEditPanel()
      hideTimespanPanel()
    },
    [
      proving,
      updateResourceRequirement,
      hideMultiEditPanel,
      hideTimespanPanel,
      selectedItems,
      refetch,
      selection,
    ]
  )

  const handleConfirmDelete = async () => {
    const ids: any[] = []
    selectedItems.forEach(async (rr) => {
      ids.push(rr._id)
    })
    const { errors } = await deleteResourceRequirements({
      variables: {
        provingId: proving._id,
        idsToDelete: ids,
      },
    })
    if (errors) {
      throw errors
    }
    selection.setAllSelected(false)
    refetch()
    hideDialog()
  }

  useCommand(
    {
      key: 'addResourceRequirement',
      priority: 10,
      text: t('model.proving.resourceRequirements.addResourceRequirement'),
      iconProps: { iconName: 'Manufacturing' },
      disabled: !canUseCommand,
      headerText: t(
        'model.proving.resourceRequirements.addResourceRequirement'
      ),
      onClick: () => {
        showPanel({
          amount: 1,
          dateTo: proving.dateTo,
          dateFrom: proving.dateFrom,
          orderIdDistribution: [],
        })
        setFormType(FormType.addResourceRequirement)
      },
    },
    [canUpdateCurrentProving]
  )
  useCommand(
    {
      key: 'editAmount',
      priority: 20,
      text: t('model.proving.resourceRequirements.editAmount'),
      iconProps: { iconName: 'NumberField' },
      disabled:
        !canUpdateCurrentProving ||
        selectedItems.length === 0 ||
        selectedItems.some(
          (sel) =>
            sel.resourceRequirementType === ResourceSelectionType.TestTrack
        ) ||
        !!isMaintenanceHead,
      onClick: () => {
        showMultiEditPanel({
          amount: selectedItems[0].amount,
        })
        setFormType(FormType.amount)
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )

  //#region TIMESPAN
  const setResourceRequirementDateToProvingDate =
    React.useCallback(async () => {
      const batch: ResourceRequirementBatchInput[] = selectedItems.map(
        (sel) => ({
          _id: sel._id,
          update: {
            dateFrom: proving.dateFrom,
            dateTo: proving.dateTo,
          },
        })
      )

      await updateResourceRequirement({
        variables: {
          provingId: proving._id,
          batch,
        },
      })

      refetch()
    }, [proving, selectedItems, updateResourceRequirement, refetch])

  useCommand(
    {
      key: 'timespan',
      priority: 30,
      text: t('model.proving.resourceRequirements.timespan'),
      iconProps: { iconName: 'CalendarWeek' },
      disabled: selectedItems.length === 0 || !canUseCommand,
      subMenuProps: {
        items: [
          {
            key: 'setResourceRequirementTimespan',
            text: t('field.timespan.set'),
            onClick: () => {
              showTimespanPanel(
                selectedItems.length === 1
                  ? {
                      dateFrom: selectedItems[0].dateFrom,
                      dateTo: selectedItems[0].dateTo,
                    }
                  : {}
              )
              setFormType(FormType.timespan)
            },
          },
          {
            key: 'setResourceRequirementDateToProvingDate',
            text: t('field.timespan.reset'),
            onClick: () => {
              setResourceRequirementDateToProvingDate()
            },
          },
        ],
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )
  useCommand(
    {
      key: 'orderId',
      priority: 40,
      text: t('model.proving.resourceRequirements.orderId'),
      hidden:
        selectedItems.some(
          (sel) =>
            ![
              ResourceSelectionType.AccessCard,
              ResourceSelectionType.FuelCard,
              ResourceSelectionType.Radio,
              ResourceSelectionType.TestTrack,
            ].includes(sel.resourceRequirementType)
        ) || selectedItems.length === 0,
      disabled:
        !canUseCommand ||
        selectedItems.some(
          (sel) =>
            ![
              ResourceSelectionType.AccessCard,
              ResourceSelectionType.FuelCard,
              ResourceSelectionType.Radio,
              ResourceSelectionType.TestTrack,
            ].includes(sel.resourceRequirementType)
        ) ||
        selectedItems.length === 0,
      iconProps: { iconName: 'DonutChart' },
      onClick: () => {
        showMultiEditPanel({
          orderIdDistribution:
            selectedItems.length === 1
              ? omitDeep(selectedItems[0].orderIdDistribution, '__typename')
              : [],
        })
        setFormType(FormType.orderId)
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )
  useCommand(
    {
      key: 'automaticResource',
      priority: 50,
      text: t('model.proving.resourceRequirements.automaticResource'),
      iconProps: { iconName: 'Shapes' },
      hidden:
        proving.status === 8 ||
        proving.status === 9 ||
        (!isResourceManager && !isAdmin && !isProvingEditor && !isServiceTeam),
      disabled: selectedItems.length !== 1,
      onClick: () => {
        setFormType(FormType.resourceSearch)
        showResourceSearchPanel(selectedItems[0])
      },
    },
    [isProvingAdmin, selectedItems]
  )
  //#region STATUS
  const handleBookingStatusNotRequiredSubmit = React.useCallback(async () => {
    const batch: ResourceRequirementBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        status: ResourceRequirementStatus.NotRequired,
      },
    }))
    await updateResourceRequirement({
      variables: {
        provingId: proving._id,
        batch,
      },
    })

    refetch()
  }, [proving, selectedItems, updateResourceRequirement, refetch])

  const handleBookingStatusOpenSubmit = React.useCallback(async () => {
    const batch: ResourceRequirementBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        status: ResourceRequirementStatus.Open,
      },
    }))

    await updateResourceRequirement({
      variables: {
        provingId: proving._id,
        batch,
      },
    })

    refetch()
  }, [proving, selectedItems, updateResourceRequirement, refetch])

  const handleBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: ResourceRequirementBatchInput[] = selectedItems.map(
        (sel) => ({
          _id: sel._id,
          update: {
            status: ResourceRequirementStatus.InProgress,
          },
        })
      )

      await updateResourceRequirement({
        variables: {
          provingId: proving._id,
          batch,
        },
      })

      refetch()
    }, [proving, selectedItems, updateResourceRequirement, refetch])

  const handleBookingStatusFinishedSubmit = React.useCallback(async () => {
    const batch: ResourceRequirementBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        status: ResourceRequirementStatus.Finished,
      },
    }))

    await updateResourceRequirement({
      variables: {
        provingId: proving._id,
        batch,
      },
    })

    refetch()
  }, [proving, selectedItems, updateResourceRequirement, refetch])

  useCommand(
    {
      key: 'status',
      priority: 60,
      text: t('model.proving.resourceRequirements.status'),
      iconProps: { iconName: 'DocumentApproval' },
      hidden: !isProvingAdmin,
      disabled:
        proving.status === 8 ||
        proving.status === 9 ||
        selectedItems.length === 0 ||
        !canUpdateStatus(),
      subMenuProps: {
        items: [
          {
            key: 'resourceRequirementBookingStatusNotRequired',
            text: t('enum.bookingStatus.notRequired'),
            onClick: () => {
              handleBookingStatusNotRequiredSubmit()
            },
          },
          {
            key: 'resourceRequirementBookingStatusOpen',
            text: t('enum.bookingStatus.open'),
            onClick: () => {
              handleBookingStatusOpenSubmit()
            },
          },
          {
            key: 'resourceRequirementBookingStatusInProgress',
            text: t('enum.bookingStatus.workInProgress'),
            onClick: () => {
              handleBookingStatusWorkInProgressSubmit()
            },
          },
          {
            key: 'resourceRequirementBookingStatusFinished',
            text: t('enum.bookingStatus.finished'),
            onClick: () => {
              handleBookingStatusFinishedSubmit()
            },
          },
        ],
      },
      // TODO: ONLY AS ADMIN
      // onClick: () => {
      //   setFormType(FormType.status)
      //   showMultiEditPanel(
      //     selectedItems.length === 1 ? { status: selectedItems[0].status } : {}
      //   )
      // },
    },
    [selectedItems, isProvingAdmin]
  )
  //#endregion STATUS
  useCommand(
    {
      key: 'nmf',
      priority: 70,
      text: t('model.proving.resourceRequirements.usageNMF'),
      iconProps: { iconName: 'NetworkTower' },
      hidden: !proving.provingGround!.showNMF,
      disabled: !canUpdateCurrentProving || !userCanUpdate(),
      onClick: () => {
        setFormType(FormType.nmf)
        showPanel({ nmf: omitDeep(proving.nmf, '__typename') || {} })
      },
    },
    [canUpdateCurrentProving, proving]
  )

  useCommand(
    {
      key: 'specialUsage',
      priority: 70,
      text: t('model.proving.resourceRequirements.specialUsage'),
      iconProps: { iconName: 'NetworkTower' },
      hidden: !proving.provingGround!.showSpecialUsage,
      disabled: !isProvingManager && !isProvingAdmin && !isResourceManager,
      onClick: () => {
        setFormType(FormType.specialUsage)
        showPanel({
          specialUsage: omitDeep(proving.specialUsage, '__typename') || {},
        })
      },
    },
    [canUpdateCurrentProving, proving]
  )

  useCommand(
    {
      key: 'check',
      priority: 80,
      text: t('panels.plausabilityCheck.title'),
      iconProps: { iconName: 'Zoom' },
      disabled: !canUseCommand,
      onClick: () => {
        setFormType(FormType.check)
        showPanel()
      },
    },
    [canUpdateCurrentProving]
  )

  useDeselectCommand(
    selectedItems.length,
    {
      priority: 0,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )
  useCommand(
    {
      key: 'delete',
      farCommand: true,
      priority: 10,
      text: t('model.proving.resourceRequirements.deleteResourceRequirement'),
      hidden: selectedItems.length === 0,
      disabled: !canUseCommand,
      iconProps: { iconName: 'Delete' },
      onClick: () => showDialog(),
    },
    [selectedItems, canUpdateCurrentProving]
  )

  const handleDetailsViewSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      // values = clearObjectIdsByPath(values, [
      //   'testTrack',
      //   'orderIdDistribution',
      // ])
      const updateValues = removeNullProperties({
        testTrack: values.testTrack,
        dateTo: values.dateTo,
        dateFrom: values.dateFrom,
        amount: values.amount,
        orderIdDistribution: values.orderIdDistribution,
        racewayHours: values.racewayHours,
        resourceRequirementType: values.resourceRequirementType,
      })
      const { errors } = await updateResourceRequirement({
        variables: {
          provingId: proving._id,
          batch: [
            {
              _id: selectedItemId,
              update: {
                dateFrom: updateValues.dateFrom,
                dateTo: updateValues.dateTo,
                racewayHours: updateValues.racewayHours,
                amount: updateValues.amount,
                orderIdDistribution: omitDeep(
                  updateValues.orderIdDistribution,
                  ['__typename']
                ),
              },
            },
          ],
        },
      })
      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideEditPanel()
    },
    [
      hideEditPanel,
      proving,
      refetch,
      selection,
      updateResourceRequirement,
      selectedItemId,
    ]
  )

  const handleAnnotationSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const { errors } = await updateProving({
        variables: {
          id: proving.id,
          update: {
            annotationResourceRequirements: values.annotation,
          },
        },
      })
      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      refetch()
      hideAnnotationPanel()
    },
    [proving, updateProving, hideAnnotationPanel, refetch]
  )

  const onItemInvoked = (item: any): void => {
    setSelectedItemId(item._id)
    showEditPanel({
      ...item,
    })
  }
  const [addResourceBooking] = useAddResourceBookingMutation()

  const handleSubmitResourceBookings = React.useCallback(
    async (
      values: any,
      actions: FormikActions<ProvingDetailsResourceBookingFormValues>
    ) => {
      values = clearObjectIdsByPath(values, [
        'testTrack',
        'orderIdDistribution',
      ])
      const itemsToAdd: ResourceBookingAddInput[] =
        values.resources !== undefined
          ? values.resources.map(
              (res: Resource) =>
                ({
                  resource: res._id,
                  dateFrom:
                    values.dateFrom === undefined
                      ? proving.dateFrom
                      : values.dateFrom,
                  dateTo:
                    values.dateTo === undefined
                      ? proving.dateTo
                      : values.dateTo,
                } as ResourceBookingAddInput)
            )
          : ([] as ResourceBookingAddInput[])

      // const item = {}
      actions.setSubmitting(true)
      const { errors } = await addResourceBooking({
        variables: {
          provingId: proving._id,
          input: itemsToAdd,
        },
      })
      if (errors) {
        throw errors
      }

      actions.setSubmitting(false)
      refetch()
      hidePanel()
    },
    [proving, addResourceBooking, hidePanel, refetch]
  )

  const iconClassGreen = mergeStyles({
    paddingTop: 3,
    fontSize: 20,
    height: 20,
    width: 20,
    color: theme.palette.green,
  })
  const iconClassRed = mergeStyles({
    paddingTop: 3,
    fontSize: 20,
    height: 20,
    width: 20,
    color: theme.palette.red,
  })
  return (
    <Page>
      <Section label={t('section.resourceRequirements.annotation')}>
        <AutoStack horizontal={true}>
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            disabled={!canUseCommand}
            onClick={() => {
              setFormType(FormType.annotation)
              showAnnotationPanel({
                annotation: proving.annotationResourceRequirements || '',
              })
            }}
          />

          <RichTextViewer
            richText={
              proving.annotationResourceRequirements
                ? proving.annotationResourceRequirements
                : ''
            }
          />
        </AutoStack>
      </Section>
      <Section label={t('section.resourceRequirements.header')}>
        <AppCommandBar />
        <ResourceRequirementList
          items={resourceRequirements}
          selection={selection}
          onItemInvoked={onItemInvoked}
        />
      </Section>
      {proving &&
        proving.provingGround &&
        proving.provingGround.showSpecialUsage &&
        proving.specialUsage &&
        (proving.specialUsage.byNight === true ||
          (proving.specialUsage && proving.specialUsage.bySaturday === true) ||
          (proving.specialUsage && proving.specialUsage.bySunday === true)) && (
          <Section label={t('model.proving.resourceRequirements.specialUsage')}>
            <Stack tokens={{ childrenGap: 4 }}>
              {proving.specialUsage &&
                proving.specialUsage.bySaturday === true && (
                  <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                    <Icon className={iconClassGreen} iconName="CheckMark" />
                    <Label>{t('field.specialUsage.bySaturday')}</Label>
                  </AutoStack>
                )}
              {proving.specialUsage &&
                proving.specialUsage.bySunday === true && (
                  <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                    <Icon className={iconClassGreen} iconName="CheckMark" />
                    <Label>{t('field.specialUsage.bySunday')}</Label>
                  </AutoStack>
                )}
              {proving.specialUsage &&
                proving.specialUsage.byNight === true && (
                  <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                    <Icon className={iconClassGreen} iconName="CheckMark" />
                    <Label>{t('field.specialUsage.byNight')}</Label>
                  </AutoStack>
                )}
            </Stack>
          </Section>
        )}
      {proving &&
        proving.provingGround &&
        proving.provingGround.showNMF &&
        proving.nmf &&
        (proving.nmf.requirement ||
          proving.nmf.informed ||
          proving.nmf.release ||
          proving.nmf.transport) && (
          <Section label={t('Nutzung mobile Ladeinfrastruktur')}>
            <Stack tokens={{ childrenGap: 4 }}>
              {proving.nmf && proving.nmf.requirement ? (
                <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                  <Icon className={iconClassGreen} iconName="CheckMark" />
                  <Label>{t('field.nmf.requirement.true')}</Label>
                </AutoStack>
              ) : (
                <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                  <Icon className={iconClassRed} iconName="Error" />
                  <Label>{t('field.nmf.requirement.false')}</Label>
                </AutoStack>
              )}
              {proving.nmf && proving.nmf.release && (
                <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                  <Icon className={iconClassGreen} iconName="CheckMark" />
                  <Label>{t('field.nmf.release.true')}</Label>
                </AutoStack>
              )}
              {proving.nmf && proving.nmf.release === false && (
                <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                  <Icon className={iconClassRed} iconName="Error" />
                  <Label>{t('field.nmf.release.false')}</Label>
                </AutoStack>
              )}
              {proving.nmf && proving.nmf.transport && (
                <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                  <Icon className={iconClassGreen} iconName="CheckMark" />
                  <Label>{t('field.nmf.transport.true')}</Label>
                </AutoStack>
              )}
              {proving.nmf && proving.nmf.transport === false && (
                <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                  <Icon className={iconClassRed} iconName="Error" />
                  <Label>{t('field.nmf.transport.false')}</Label>
                </AutoStack>
              )}

              {proving.nmf && proving.nmf.informed && (
                <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                  <Icon className={iconClassGreen} iconName="CheckMark" />
                  <Label>{t('field.nmf.informed.true')}</Label>
                </AutoStack>
              )}
              {proving.nmf && proving.nmf.informed === false && (
                <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                  <Icon className={iconClassRed} iconName="Error" />
                  <Label>{t('field.nmf.informed.false')}</Label>
                </AutoStack>
              )}
            </Stack>
          </Section>
        )}
      <Section label={t('section.participantOverview.header')}>
        <ParticipantList
          stickyHeader={false}
          items={participants}
          readonly={true}
          provingGround={proving.provingGround}
        />
      </Section>

      {formType !== undefined &&
        (formType === FormType.addResourceRequirement ? (
          <FormikPanel
            {...panelState}
            onSubmit={handleSubmit}
            validationSchema={new ProvingDetailsResourceRequirementFormSchema()}
          >
            <ProvingDetailsResourceRequirementForm
              proving={proving}
              testTracks={testTracks}
              testTrackUsages={testTrackUsages}
              fuels={fuels}
              chargingInfrastructures={chargingInfrastructures}
            />
          </FormikPanel>
        ) : (
          <FormikPanel
            {...multiEditPanelState}
            onSubmit={handleSubmitMulti}
            validationSchema={
              formType === FormType.orderId
                ? new ProvingDetailsOrderIdDistributionFormSchema()
                : formType === FormType.amount
                ? AmountFormSchema
                : undefined
            }
            {...orderIdInitialValues}
          >
            {formType === FormType.amount && <ProvingDetailsAmountForm />}

            {formType === FormType.orderId && (
              <ProvingDetailsOrderIdDistributionForm
                orderIds={proving.orderIdDistribution!.map(
                  (x: any) => x.orderId!
                )}
                formType={
                  selectedItems.every(
                    (sel) =>
                      sel.resourceRequirementType ===
                      ResourceSelectionType.TestTrack
                  )
                    ? OrderIdDistributionFormType.MultipleOrderIdDistribution
                    : OrderIdDistributionFormType.SingleOrderId
                }
              />
            )}
            {formType === FormType.status && (
              <ProvingDetailsResourceRequirementStatusForm />
            )}
          </FormikPanel>
        ))}
      {formType === FormType.nmf && (
        <FormikPanel {...panelState} onSubmit={handleSubmitNmf}>
          <ProvingDetailsNmfForm proving={proving} />
        </FormikPanel>
      )}
      {formType === FormType.specialUsage && (
        <FormikPanel {...panelState} onSubmit={handleSubmitSpecialUsage}>
          <ProvingDetailsSpecialUsageForm proving={proving} />
        </FormikPanel>
      )}
      <FormikPanel {...annotationPanelState} onSubmit={handleAnnotationSubmit}>
        {formType !== undefined && formType === FormType.annotation && (
          <ProvingDetailsAnnotationForm />
        )}
      </FormikPanel>
      <FormikPanel
        {...timespanPanelState}
        onSubmit={handleSubmitMulti}
        validationSchema={
          new ProvingDetailsTimespanFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {formType === FormType.timespan && (
          <ProvingDetailsTimespanForm proving={proving} />
        )}
      </FormikPanel>
      <NormalPanel
        type={PanelType.extraLarge}
        {...resourceSearchPanelState}
        onSubmit={handleSubmitResourceBookings}
      >
        {formType === FormType.resourceSearch && <ResourceSearchForm />}
      </NormalPanel>
      {formType === FormType.check && (
        <Panel
          type={PanelType.medium}
          headerText={t('panels.plausabilityCheck.title')}
          closeButtonAriaLabel={t('action.close')}
          isLightDismiss={true}
          onDismiss={() => setFormType(FormType.undefined)}
          isOpen={formType === FormType.check}
        >
          <ProvingDetailsPlausabilityCheckForm proving={proving} />
        </Panel>
      )}
      <FormikPanel
        {...editPanelState}
        onSubmit={handleDetailsViewSubmit}
        type={PanelType.large}
        validationSchema={new ResourceRequirementDetailsFormSchema()}
      >
        <ResourceRequirementDetailsForm />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
      {/* <Section label={t('section.debug.header')}>
        <pre>{JSON.stringify(proving, null, 2)}</pre>
      </Section> */}
    </Page>
  )
}
